import { zodResolver } from '@hookform/resolvers/zod';
import {
  activityLevelSchema,
  ageFromBirthDate,
  birthDateSchema,
  goalSchema,
  macroTargets,
  profileInputSchema,
  sexSchema,
  targetCalories,
  tdee,
  type ActivityLevel,
  type Goal,
  type Sex,
} from '@fitness/core';
import type { Profile } from '@fitness/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Ban, ChevronRight, Compass, KeyRound, Lock, LogOut, UserRound } from 'lucide-react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ErrorBanner } from '@/components/ErrorBanner';
import { Field, fieldAria } from '@/components/Field';
import { LogoutPendingDialog } from '@/components/LogoutPendingDialog';
import { Input, Select } from '@/components/Input';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { RemindersPanel } from '@/components/RemindersPanel';
import { disablePush, hasPushRegistration } from '@/lib/push';
import { Avatar } from '@/components/community/Avatar';
import { api, describeError } from '@/lib/api';
import { SAVED, showToast } from '@/lib/toast';
import { useAuth } from '@/lib/auth';
import { TOURS, TOUR_UI } from '@/lib/i18n/tours';
import { useTour } from '@/lib/tour/context';
import { displayStatus } from '@/lib/tour/progress';
import { pendingChanges } from '@/lib/offline/syncQueue';
import { queryKeys } from '@/lib/queryClient';

export const Route = createFileRoute('/_app/settings/')({
  component: SettingsPage,
});

// --- Formulario -----------------------------------------------------------

/** `''` significa «sin indicar»: el perfil admite nulos en todos sus campos. */
const BLANK = z.literal('');

/** Texto numérico opcional, validado contra un rango. */
function numericText(min: number, max: number, message: string, integer = false) {
  return z.string().refine((raw) => {
    const value = raw.trim();
    if (value === '') return true;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return false;
    if (integer && !Number.isInteger(parsed)) return false;
    return parsed >= min && parsed <= max;
  }, message);
}

/**
 * Esquema del formulario: todos los campos son texto porque eso es lo que
 * devuelven los `<input>` y `<select>` del navegador. Al enviar se convierten a
 * la forma del contrato y se validan otra vez con `profileInputSchema`.
 */
const profileFormSchema = z.object({
  sex: z.union([BLANK, sexSchema]),
  birthDate: z.union([BLANK, birthDateSchema]),
  heightCm: numericText(50, 260, 'Introduce una altura entre 50 y 260 cm.'),
  activityLevel: z.union([BLANK, activityLevelSchema]),
  goal: z.union([BLANK, goalSchema]),
  trainingDaysPerWeek: numericText(0, 7, 'Indica entre 0 y 7 días por semana.', true),
});

type ProfileFormValues = z.infer<typeof profileFormSchema>;

const SEX_OPTIONS: ReadonlyArray<{ value: Sex; label: string }> = [
  { value: 'female', label: 'Mujer' },
  { value: 'male', label: 'Hombre' },
  { value: 'other', label: 'Otro / prefiero no decirlo' },
];

const ACTIVITY_OPTIONS: ReadonlyArray<{ value: ActivityLevel; label: string }> = [
  { value: 'sedentary', label: 'Sedentario (trabajo sentado, sin ejercicio)' },
  { value: 'light', label: 'Ligero (1-3 días de ejercicio)' },
  { value: 'moderate', label: 'Moderado (3-5 días)' },
  { value: 'active', label: 'Activo (6-7 días)' },
  { value: 'very_active', label: 'Muy activo (trabajo físico o doble sesión)' },
];

const GOAL_OPTIONS: ReadonlyArray<{ value: Goal; label: string }> = [
  { value: 'lose', label: 'Perder grasa' },
  { value: 'maintain', label: 'Mantenerme' },
  { value: 'gain', label: 'Ganar masa' },
];

function toFormValues(profile: Profile): ProfileFormValues {
  return {
    sex: profile.sex ?? '',
    birthDate: profile.birthDate ?? '',
    heightCm: profile.heightCm === null ? '' : String(profile.heightCm),
    activityLevel: profile.activityLevel ?? '',
    goal: profile.goal ?? '',
    trainingDaysPerWeek:
      profile.trainingDaysPerWeek === null ? '' : String(profile.trainingDaysPerWeek),
  };
}

function toNullableNumber(raw: string): number | null {
  const value = raw.trim();
  return value === '' ? null : Number(value);
}

// --- Página ---------------------------------------------------------------

function SettingsPage() {
  const { user, status, logout } = useAuth();
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);
  // Cambios sin subir al pedir cerrar sesión: si hay, se avisa antes.
  const [pendingOnLogout, setPendingOnLogout] = useState<number | null>(null);

  // Entre cerrar sesión y llegar a `/login` esta pantalla sigue montada un
  // instante: sin `enabled` volvería a pedir datos ya sin token.
  const authenticated = status === 'authenticated';

  const profileQuery = useQuery({
    queryKey: queryKeys.profile,
    queryFn: () => api.me.getProfile(),
    enabled: authenticated,
  });

  // La API devuelve las mediciones de la más reciente a la más antigua, así que
  // con las últimas basta para encontrar el peso más actual.
  const metricsQuery = useQuery({
    queryKey: queryKeys.bodyMetrics,
    queryFn: () => api.me.listBodyMetrics({ limit: 10 }),
    enabled: authenticated,
  });

  const { progress: tourProgress } = useTour();
  const toursDone = TOURS.filter((tour) => displayStatus(tourProgress[tour.id]) === 'done').length;

  const lastWeightKg =
    metricsQuery.data?.find((metric) => metric.weightKg !== null)?.weightKg ?? null;

  async function requestLogout(): Promise<void> {
    const pending = await pendingChanges().catch(() => 0);
    if (pending > 0) {
      setPendingOnLogout(pending);
      return;
    }
    await handleLogout();
  }

  async function handleLogout(): Promise<void> {
    setPendingOnLogout(null);
    setLogoutError(null);
    setLoggingOut(true);
    try {
      if (user && hasPushRegistration(user.id)) await disablePush(user.id).catch(() => {});
      await logout();
      await navigate({ to: '/login', replace: true });
    } catch (error) {
      setLogoutError(describeError(error));
      setLoggingOut(false);
    }
  }

  return (
    <>
      <PageHeader title="Ajustes" subtitle="Tu cuenta, tu perfil y tus accesos." />

      <div className="flex flex-col gap-4">
        <Link
          to="/settings/guide"
          className="flex min-h-14 items-center gap-3 rounded-2xl border border-line bg-surface-raised px-4 py-3 transition-colors hover:bg-surface-overlay"
        >
          <Compass className="size-5 shrink-0 text-brand" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="block font-medium">{TOUR_UI.pageTitle}</span>
            <span className="block text-sm text-ink-muted">
              {TOUR_UI.entryDescription(toursDone, TOURS.length)}
            </span>
          </span>
          <ChevronRight className="size-5 shrink-0 text-ink-faint" aria-hidden="true" />
        </Link>
        <Card>
          <div className="flex items-center gap-3">
            <Avatar url={user?.avatarUrl ?? null} name={user?.displayName ?? '?'} size="md" />
            <div className="min-w-0">
              <p className="line-clamp-2 text-base font-semibold">{user?.displayName}</p>
              <p className="truncate text-sm text-brand">@{user?.handle}</p>
              <p className="truncate text-sm text-ink-muted">{user?.email}</p>
            </div>
          </div>
        </Card>

        <Link
          to="/settings/profile"
          className="flex min-h-14 items-center gap-3 rounded-2xl border border-line bg-surface-raised px-4 transition-colors hover:bg-surface-overlay"
        >
          <UserRound className="size-5 shrink-0 text-brand" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="block font-medium">Perfil público</span>
            <span className="block text-sm text-ink-muted">
              {user?.isPublic === true
                ? 'Visible para la comunidad: foto, presentación y publicaciones.'
                : 'Ahora mismo es privado: los demás sólo ven tu nombre y tu @.'}
            </span>
          </span>
          <ChevronRight className="size-5 shrink-0 text-ink-faint" aria-hidden="true" />
        </Link>

        <Link
          to="/settings/blocks"
          className="flex min-h-14 items-center gap-3 rounded-2xl border border-line bg-surface-raised px-4 transition-colors hover:bg-surface-overlay"
        >
          <Ban className="size-5 shrink-0 text-brand" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="block font-medium">Cuentas bloqueadas</span>
            <span className="block text-sm text-ink-muted">
              Quién no puede verte ni escribirte.
            </span>
          </span>
          <ChevronRight className="size-5 shrink-0 text-ink-faint" aria-hidden="true" />
        </Link>

        <Link
          to="/settings/tokens"
          data-tour="settings-tokens"
          className="flex min-h-14 items-center gap-3 rounded-2xl border border-line bg-surface-raised px-4 transition-colors hover:bg-surface-overlay"
        >
          <KeyRound className="size-5 shrink-0 text-brand" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="block font-medium">Tokens de acceso</span>
            <span className="block text-sm text-ink-muted">
              Para conectar Claude, Codex u otros asistentes de IA a tu cuenta.
            </span>
          </span>
          <ChevronRight className="size-5 shrink-0 text-ink-faint" aria-hidden="true" />
        </Link>

        <Link
          to="/settings/account"
          className="flex min-h-14 items-center gap-3 rounded-2xl border border-line bg-surface-raised px-4 transition-colors hover:bg-surface-overlay"
        >
          <Lock className="size-5 shrink-0 text-brand" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="block font-medium">Cuenta</span>
            <span className="block text-sm text-ink-muted">
              Cómo entras (contraseña, Google, Apple) o borrar la cuenta.
            </span>
          </span>
          <ChevronRight className="size-5 shrink-0 text-ink-faint" aria-hidden="true" />
        </Link>

        {profileQuery.isPending ? (
          <Card>
            <div className="flex items-center gap-3 text-ink-muted">
              <Spinner size="sm" />
              <span className="text-sm">Cargando tu perfil…</span>
            </div>
          </Card>
        ) : profileQuery.isError ? (
          <ErrorBanner
            title="No hemos podido cargar tu perfil"
            error={profileQuery.error}
            onRetry={() => void profileQuery.refetch()}
          />
        ) : (
          <ProfileForm
            profile={profileQuery.data}
            lastWeightKg={lastWeightKg}
            weightPending={metricsQuery.isPending}
          />
        )}

        {authenticated && <RemindersPanel />}

        {logoutError ? <ErrorBanner message={logoutError} /> : null}

        <Button
          variant="secondary"
          size="lg"
          block
          loading={loggingOut}
          onClick={() => void requestLogout()}
          icon={<LogOut className="size-4" />}
        >
          Cerrar sesión
        </Button>
      </div>

      {pendingOnLogout !== null ? (
        <LogoutPendingDialog
          count={pendingOnLogout}
          onConfirm={() => void handleLogout()}
          onCancel={() => setPendingOnLogout(null)}
        />
      ) : null}
    </>
  );
}

// --- Perfil ---------------------------------------------------------------

interface ProfileFormProps {
  profile: Profile;
  /** Último peso registrado en métricas corporales, si lo hay. */
  lastWeightKg: number | null;
  weightPending: boolean;
}

function ProfileForm({ profile, lastWeightKg, weightPending }: ProfileFormProps) {
  const queryClient = useQueryClient();
  // Si todavía no hay ninguna medición corporal, el peso se pide aquí y sólo se
  // usa para la estimación: no forma parte del perfil que guarda el servidor.
  const [localWeight, setLocalWeight] = useState('');

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(profileFormSchema),
    defaultValues: toFormValues(profile),
  });

  const watched = useWatch({ control });

  const mutation = useMutation({
    mutationFn: async (values: ProfileFormValues) => {
      const payload = profileInputSchema.parse({
        sex: values.sex === '' ? null : values.sex,
        birthDate: values.birthDate === '' ? null : values.birthDate,
        heightCm: toNullableNumber(values.heightCm),
        activityLevel: values.activityLevel === '' ? null : values.activityLevel,
        goal: values.goal === '' ? null : values.goal,
        trainingDaysPerWeek: toNullableNumber(values.trainingDaysPerWeek),
        // `PUT` reemplaza el perfil entero: estas listas todavía no se editan
        // aquí, así que se reenvían tal y como estaban.
        dietaryPreferences: profile.dietaryPreferences,
        allergies: profile.allergies,
        equipment: profile.equipment,
      });
      return api.me.putProfile(payload);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.profile, updated);
      reset(toFormValues(updated));
      showToast(SAVED.profileSaved);
    },
  });

  const weightKg = lastWeightKg ?? toNullableNumber(localWeight);
  const estimate = estimateEnergy({
    sex: watched.sex,
    birthDate: watched.birthDate,
    heightCm: watched.heightCm,
    activityLevel: watched.activityLevel,
    goal: watched.goal,
    weightKg,
  });

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values);
  });

  return (
    <Card
      title="Tu perfil"
      description="Con estos datos calculamos tus calorías y tus macros objetivo."
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {mutation.isError ? (
          <ErrorBanner
            title="No se ha podido guardar"
            error={mutation.error}
            errorOptions={{ saving: true }}
          />
        ) : null}

        <Field htmlFor="sex" label="Sexo" error={errors.sex?.message}>
          <Select {...fieldAria('sex', errors.sex?.message)} {...register('sex')}>
            <option value="">Sin indicar</option>
            {SEX_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field htmlFor="birthDate" label="Fecha de nacimiento" error={errors.birthDate?.message}>
          <Input
            {...fieldAria('birthDate', errors.birthDate?.message)}
            type="date"
            max={new Date().toISOString().slice(0, 10)}
            {...register('birthDate')}
          />
        </Field>

        <Field htmlFor="heightCm" label="Altura (cm)" error={errors.heightCm?.message}>
          <Input
            {...fieldAria('heightCm', errors.heightCm?.message)}
            type="number"
            inputMode="numeric"
            min={50}
            max={260}
            step={1}
            placeholder="170"
            {...register('heightCm')}
          />
        </Field>

        <Field
          htmlFor="activityLevel"
          label="Nivel de actividad"
          error={errors.activityLevel?.message}
        >
          <Select
            {...fieldAria('activityLevel', errors.activityLevel?.message)}
            {...register('activityLevel')}
          >
            <option value="">Sin indicar</option>
            {ACTIVITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field htmlFor="goal" label="Objetivo" error={errors.goal?.message}>
          <Select {...fieldAria('goal', errors.goal?.message)} {...register('goal')}>
            <option value="">Sin indicar</option>
            {GOAL_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          htmlFor="trainingDaysPerWeek"
          label="Días de entrenamiento por semana"
          error={errors.trainingDaysPerWeek?.message}
        >
          <Input
            {...fieldAria('trainingDaysPerWeek', errors.trainingDaysPerWeek?.message)}
            type="number"
            inputMode="numeric"
            min={0}
            max={7}
            step={1}
            placeholder="4"
            {...register('trainingDaysPerWeek')}
          />
        </Field>

        <div className="flex items-center gap-3">
          <Button type="submit" size="lg" loading={mutation.isPending} disabled={!isDirty}>
            Guardar perfil
          </Button>
        </div>
      </form>

      <hr className="my-5 border-line" />

      <EnergyEstimatePanel
        estimate={estimate}
        lastWeightKg={lastWeightKg}
        weightPending={weightPending}
        localWeight={localWeight}
        onLocalWeightChange={setLocalWeight}
      />
    </Card>
  );
}

// --- Estimación energética ------------------------------------------------

interface EnergyEstimate {
  tdee: number;
  calories: number;
  proteinG: number;
  fatG: number;
  carbsG: number;
  goal: Goal;
  ageYears: number;
}

interface EstimateInput {
  sex: string | undefined;
  birthDate: string | undefined;
  heightCm: string | undefined;
  activityLevel: string | undefined;
  goal: string | undefined;
  weightKg: number | null;
}

/**
 * Calcula el gasto y los macros objetivo con `@fitness/core`, o devuelve `null`
 * si aún falta algún dato. Nunca lanza: los valores llegan de un formulario a
 * medio rellenar.
 */
function estimateEnergy(input: EstimateInput): EnergyEstimate | null {
  const sex = sexSchema.safeParse(input.sex);
  const activityLevel = activityLevelSchema.safeParse(input.activityLevel);
  const heightCm = Number((input.heightCm ?? '').trim());
  const weightKg = input.weightKg;

  if (!sex.success || !activityLevel.success) return null;
  if (!Number.isFinite(heightCm) || heightCm <= 0) return null;
  if (weightKg === null || !Number.isFinite(weightKg) || weightKg <= 0) return null;

  let ageYears: number;
  try {
    ageYears = ageFromBirthDate(input.birthDate ?? '');
  } catch {
    return null;
  }

  // El objetivo sólo afecta a las calorías y a los macros, no al gasto.
  const goal: Goal = goalSchema.safeParse(input.goal).data ?? 'maintain';
  const total = tdee({
    sex: sex.data,
    weightKg,
    heightCm,
    ageYears,
    activityLevel: activityLevel.data,
  });
  const calories = targetCalories({ tdee: total, goal });
  const macros = macroTargets({ calories, weightKg, goal });

  return { tdee: total, goal, ageYears, ...macros };
}

interface EnergyEstimatePanelProps {
  estimate: EnergyEstimate | null;
  lastWeightKg: number | null;
  weightPending: boolean;
  localWeight: string;
  onLocalWeightChange: (value: string) => void;
}

const GOAL_LABEL: Record<Goal, string> = {
  lose: 'perder grasa',
  maintain: 'mantenerte',
  gain: 'ganar masa',
};

function EnergyEstimatePanel({
  estimate,
  lastWeightKg,
  weightPending,
  localWeight,
  onLocalWeightChange,
}: EnergyEstimatePanelProps) {
  return (
    <section>
      <h3 className="text-base font-semibold">Gasto estimado</h3>

      {lastWeightKg === null ? (
        <div className="mt-3">
          <Field
            htmlFor="localWeight"
            label="Peso actual (kg)"
            hint={
              weightPending
                ? 'Buscando tu última medición…'
                : 'Aún no has registrado ninguna medición: este peso sólo se usa para el cálculo.'
            }
          >
            <Input
              {...fieldAria('localWeight', undefined, true)}
              type="number"
              inputMode="decimal"
              min={20}
              max={400}
              step={0.1}
              placeholder="70"
              value={localWeight}
              onChange={(event) => onLocalWeightChange(event.target.value)}
            />
          </Field>
        </div>
      ) : (
        <p className="mt-1 text-sm text-ink-muted">
          Calculado con tu última medición: <strong className="text-ink">{lastWeightKg} kg</strong>.
        </p>
      )}

      {estimate === null ? (
        <p className="mt-3 text-sm text-ink-muted">
          Rellena sexo, fecha de nacimiento, altura, nivel de actividad y peso para ver tu gasto
          diario estimado.
        </p>
      ) : (
        <div className="mt-3 flex flex-col gap-3">
          <div className="rounded-xl bg-surface-overlay p-4">
            <p className="text-sm text-ink-muted">Gasto diario (TDEE)</p>
            <p className="text-3xl font-bold text-brand">
              {estimate.tdee.toLocaleString('es-ES')} <span className="text-base">kcal</span>
            </p>
            <p className="mt-1 text-sm text-ink-muted">
              Para {GOAL_LABEL[estimate.goal]}:{' '}
              <strong className="text-ink">{estimate.calories.toLocaleString('es-ES')} kcal</strong>{' '}
              al día
            </p>
          </div>
          <dl className="grid grid-cols-3 gap-2 text-center">
            <MacroCell label="Proteína" grams={estimate.proteinG} />
            <MacroCell label="Grasa" grams={estimate.fatG} />
            <MacroCell label="Hidratos" grams={estimate.carbsG} />
          </dl>
          <p className="text-xs text-ink-faint">
            Estimación con Mifflin-St Jeor sobre {estimate.ageYears} años. Es un punto de partida,
            no una pauta médica.
          </p>
        </div>
      )}
    </section>
  );
}

function MacroCell({ label, grams }: { label: string; grams: number }) {
  return (
    <div className="rounded-xl border border-line p-3">
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="mt-0.5 text-lg font-semibold">{grams} g</dd>
    </div>
  );
}
